import { Link } from "react-router-dom";
import { useState } from "react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";
import { useInstallPrompt } from "../hooks/useInstallPrompt";

export default function Landing({ user }) {
  const { isInstallable, promptInstall, isSafari, isIOS, isInstalled } = useInstallPrompt();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSafariTip, setShowSafariTip] = useState(false);

  return (
    <div className="min-h-screen bg-[#FDFBF7] texture-overlay flex flex-col">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-3 sm:py-4 bg-[#FDFBF7]/95 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-xl sm:text-2xl font-light tracking-wide text-[#2D2A26]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            the soft ritual
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button 
                  data-testid="dashboard-btn"
                  className="rounded-full px-6 py-2 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
                >
                  Enter
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    data-testid="login-btn"
                    className="rounded-full px-6 text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]"
                  >
                    Sign in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    data-testid="get-started-btn"
                    className="rounded-full px-6 py-2 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
                  >
                    Begin
                  </Button>
                </Link>
                <Link to="/blog">
                  <Button 
                    variant="ghost" 
                    className="rounded-full px-6 text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]"
                  >
                    Ritual Edit
                  </Button>
                </Link>
                <Link to="/store">
                  <Button 
                    variant="ghost" 
                    className="rounded-full px-6 text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]"
                  >
                    Shop
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-[#4A4744] hover:text-[#2D2A26] transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-[#FDFBF7] border-t border-[#E6E2DD] shadow-lg">
            <div className="flex flex-col p-4 gap-2">
              {user ? (
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    data-testid="mobile-dashboard-btn"
                    className="w-full rounded-full py-3 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
                  >
                    Enter
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button 
                      data-testid="mobile-get-started-btn"
                      className="w-full rounded-full py-3 bg-[#A87C6D] hover:bg-[#956d5f] text-white"
                    >
                      Begin
                    </Button>
                  </Link>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button 
                      variant="ghost" 
                      data-testid="mobile-login-btn"
                      className="w-full rounded-full py-3 text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]"
                    >
                      Sign in
                    </Button>
                  </Link>
                  <Link to="/blog" onClick={() => setMobileMenuOpen(false)}>
                    <Button 
                      variant="ghost" 
                      className="w-full rounded-full py-3 text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]"
                    >
                      Ritual Edit
                    </Button>
                  </Link>
                  <Link to="/store" onClick={() => setMobileMenuOpen(false)}>
                    <Button 
                      variant="ghost" 
                      className="w-full rounded-full py-3 text-[#4A4744] hover:text-[#2D2A26] hover:bg-[#E6E2DD]"
                    >
                      Shop
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="flex-1 flex items-center justify-center px-4 sm:px-6 pt-24 sm:pt-20 pb-8">
        <div className="max-w-2xl mx-auto text-center animate-fade-in">
          <p className="text-[#A87C6D] uppercase tracking-[0.2em] sm:tracking-[0.3em] text-xs sm:text-sm mb-4 sm:mb-6 font-medium">
            A gentle companion
          </p>
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-[#2D2A26] leading-[1.15] sm:leading-[1.1] mb-6 sm:mb-8"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            A quiet space to feel,<br />reflect, and return<br />to yourself
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-[#4A4744] font-light leading-relaxed mb-4 sm:mb-6 max-w-lg mx-auto px-2">
            Not therapy. Not journaling. Not coaching.<br />
            Just gentle presence and permission to pause.
          </p>
          <p className="text-sm sm:text-base text-[#6E6A66] font-light italic mb-6 sm:mb-8 max-w-md mx-auto px-4">
            ritual: a small, intentional act that helps you pause, process, and choose how you want to move forward.
          </p>
          
          {!user && (
            <p className="text-[#6E6A66] text-sm mb-8 font-light">
              Take a moment to learn about The Soft Ritual before you begin.
            </p>
          )}
          
          <div className="flex flex-col items-center justify-center gap-4">
            <Link to={user ? "/dashboard" : "/discover"}>
              <Button 
                data-testid="hero-cta-btn"
                className="rounded-full px-10 py-6 text-lg bg-[#A87C6D] hover:bg-[#956d5f] text-white transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              >
                Step inside
              </Button>
            </Link>
            
            {/* Install button for Chrome/Edge */}
            {isInstallable && (
              <button 
                onClick={promptInstall}
                data-testid="install-app-btn"
                className="flex items-center gap-1.5 text-sm text-[#6E6A66] hover:text-[#A87C6D] transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Install as app
              </button>
            )}
            
            {/* Safari install instructions */}
            {isSafari && !isInstalled && (
              <div className="relative">
                <button 
                  onClick={() => setShowSafariTip(!showSafariTip)}
                  data-testid="safari-install-btn"
                  className="flex items-center gap-1.5 text-sm text-[#6E6A66] hover:text-[#A87C6D] transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Install as app
                </button>
                
                {showSafariTip && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 p-4 bg-white rounded-xl shadow-lg border border-[#E6E2DD] text-left z-50">
                    <button 
                      onClick={() => setShowSafariTip(false)}
                      className="absolute top-2 right-2 text-[#6E6A66] hover:text-[#4A4744]"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <p className="text-sm font-medium text-[#2D2A26] mb-2">
                      {isIOS ? "Add to Home Screen" : "Install on Safari"}
                    </p>
                    <p className="text-xs text-[#4A4744] leading-relaxed">
                      {isIOS ? (
                        <>
                          Tap the <span className="inline-flex items-center mx-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                          </span> share button, then scroll down and tap <strong>&ldquo;Add to Home Screen&rdquo;</strong>
                        </>
                      ) : (
                        <>
                          Click <strong>File</strong> in the menu bar, then select <strong>&ldquo;Add to Dock&rdquo;</strong> to install this app.
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
